import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from 'axios';
import heic2any from 'heic2any';
import AppURL from "./AppUrl";


const EstimationModal = ({ language, isOpen, onClose }: { language: string; isOpen: boolean; onClose: () => void }) => {
    const location = useLocation();
    const [type, setType] = useState("Mécanique");
    const [formData, setFormData] = useState({
        type: "",
        fullName: "",
        email: "",
        phone: "",
        brand: "",
        model: "",
        trim: "",
        year: "",
        description: "",
        preferredLanguage: 'fr', // Nouveau champ
        contactMethod: 'email',   // Nouveau champ
        images: [] as File[],
    });
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Déterminer le type de modal (Mécanique ou Carrosserie)
    useEffect(() => {
        if (location.pathname.includes("services-mecanique")) {
            setType("Mécanique");
        } else if (location.pathname.includes("services-carrosserie")) {
            setType("Carrosserie");
        }
    }, [location.pathname]);

    // Définition explicite du type pour éviter l'erreur TypeScript
    const vehicleBrands: Record<string, string[]> = {
        "Alfa Romeo": ["4C", "Giulia", "Giulietta", "Spider", "Stelvio", "Tonale"],
        "Audi": ["A3", "A4", "A5", "A6", "A7", "A8", "Q3", "Q5", "Q5 Sportback", "Q7", "Q8", "R8", "RS3", "RS4", "RS5", "RS6", "RS7", "RS Q8", "S3", "S4", "S5", "S6", "S7", "S8", "TT"],
        "BMW": ["1 Series", "2 Series", "3 Series", "4 Series", "5 Series", "6 Series", "7 Series", "8 Series", "i3", "i4", "i7", "i8", "iX", "M2", "M3", "M4", "M5", "M8", "X1", "X2", "X3", "X4", "X5", "X6", "X7", "Z4"],
        "Chevrolet": ["Blazer", "Bolt EV", "Camaro", "Colorado", "Corvette", "Cruze", "Equinox", "Express", "Impala", "Malibu", "Silverado", "Sonic", "Spark", "Suburban", "Tahoe", "Trailblazer", "Traverse", "Volt"],
        "Fiat": ["124 Spider", "500", "500L", "500X", "Doblo", "Freemont", "Linea", "Panda", "Punto", "Tipo"],
        "Ford": ["Bronco", "EcoSport", "Edge", "Escape", "Expedition", "Explorer", "F-150", "Fiesta", "Focus", "Fusion", "Maverick", "Mustang", "Mustang Mach-E", "Ranger", "Transit", "Super Duty"],
        "Honda": ["Accord", "Civic", "Clarity", "CR-V", "Element", "Fit", "HR-V", "Insight", "Odyssey", "Passport", "Pilot", "Ridgeline"],
        "Hyundai": ["Accent", "Elantra", "Genesis G70", "Genesis G80", "Genesis G90", "Genesis GV70", "Genesis GV80", "Ioniq", "Ioniq 5", "Kona", "Nexo", "Palisade", "Santa Cruz", "Santa Fe", "Sonata", "Tucson", "Venue", "Veloster"],
        "Jaguar": ["E-Pace", "F-Pace", "F-Type", "I-Pace", "S-Type", "XE", "XF", "XJ", "XK"],
        "Kia": ["Carnival", "Cadenza", "EV6", "Forte", "K5", "K900", "Niro", "Optima", "Rio", "Sedona", "Seltos", "Sorento", "Soul", "Sportage", "Stinger", "Telluride"],
        "Land Rover": ["Defender", "Discovery", "Discovery Sport", "Evoque", "Freelander", "Range Rover", "Range Rover Evoque", "Range Rover Sport", "Range Rover Velar", "Velar"],
        "Lexus": ["CT", "ES", "GS", "GX", "HS", "IS", "LC", "LS", "LX", "NX", "RC", "RX", "SC", "UX"],
        "Mazda": ["CX-3", "CX-30", "CX-5", "CX-9", "CX-50", "CX-90", "Mazda2", "Mazda3", "Mazda6", "MX-5 Miata", "MX-30"],
        "Mercedes-Benz": ["A-Class", "B-Class", "C-Class", "CLA", "CLS", "E-Class", "EQB", "EQC", "EQE", "EQS", "G-Class", "GLA", "GLB", "GLC", "GLE", "GLK", "GLS", "Maybach", "S-Class", "SL", "SLC", "V-Class"],
        "Mitsubishi": ["ASX", "Colt", "Eclipse Cross", "L200", "Lancer", "Mirage", "Montero Sport", "Outlander", "Pajero"],
        "Nissan": ["370Z", "Altima", "Armada", "Frontier", "GT-R", "Juke", "Kicks", "Leaf", "Maxima", "Murano", "NV200", "NV3500", "Pathfinder", "Rogue", "Sentra", "Titan", "Versa", "Xterra"],
        "Porsche": ["911", "Boxster", "Cayenne", "Cayman", "Macan", "Panamera", "Taycan"],
        "Subaru": ["Ascent", "BRZ", "Crosstrek", "Forester", "Impreza", "Legacy", "Outback", "Solterra", "WRX", "XV"],
        "Tesla": ["Cybertruck", "Model 3", "Model S", "Model X", "Model Y", "Roadster", "Semi", "Tesla Roadster 2.0"],
        "Toyota": ["4Runner", "Avalon", "C-HR", "Camry", "Corolla", "Corolla Cross", "GR86", "Highlander", "Land Cruiser", "Mirai", "Prius", "RAV4", "Sequoia", "Sienna", "Supra", "Tacoma", "Tundra", "Venza", "Yaris"],
        "Volkswagen": ["Arteon", "Atlas", "Beetle", "Golf", "ID.3", "ID.4", "ID. Buzz", "Jetta", "Passat", "Phaeton", "Polo", "T-Roc", "Tiguan", "Touareg", "Touran", "Up"],
        "Volvo": ["C40", "S60", "S90", "V60", "V90", "XC40", "XC60", "XC90"]
    }

    // Gérer les champs texte
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    // Gérer les listes déroulantes
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    // Gérer les images uploadées
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files).slice(0, 5);

            const convertedFiles = await Promise.all(
                filesArray.map(async (file) => {
                    // Vérifier si c'est un HEIC
                    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
                        try {
                            const convertedResult = await heic2any({
                                blob: file,
                                toType: 'image/jpeg', // Conversion en JPEG
                                quality: 0.8 // Qualité réduite pour optimiser la taille
                            });

                            // heic2any may return a Blob or an array of Blobs
                            const convertedBlob = Array.isArray(convertedResult) ? convertedResult[0] : convertedResult;

                            return new File([convertedBlob], file.name.replace(/\.heic$/i, '.jpg'), {
                                type: 'image/jpeg'
                            });
                        } catch (err) {
                            console.error("Échec de la conversion HEIC :", err);
                            return file; // Fallback (gestion d'erreur)
                        }
                    }
                    return file; // Si ce n'est pas un HEIC, garder le fichier original
                })
            );

            setFormData((prev) => ({ ...prev, images: convertedFiles }));
        }
    };

    const validateForm = () => {
        // Vérification des champs obligatoires
        const requiredFields = [
            formData.fullName,
            formData.email,
            formData.phone,
            formData.brand,
            formData.model,
            formData.trim,
            formData.year,
            formData.description,
            formData.preferredLanguage,
            formData.contactMethod,

        ];

        if (requiredFields.some(field => !field)) {
            setError(language === "fr"
                ? "Tous les champs sont obligatoires."
                : "All fields are required.");
            return false;
        }

        // Vérification du nombre d'images si c'est pour la carrosserie
        if (type === "Carrosserie" && formData.images.length < 1) {
            setError(language === "fr"
                ? "Veuillez télécharger au moins une image pour une estimation en carrosserie."
                : "Please upload at least one image for a bodywork estimate.");
            return false;
        }

        setError("");
        return true;
    };



    const successMessages = {
        fr: "Votre demande d'estimation a été bien reçue. Nous vous répondrons dans les plus brefs délais.",
        en: "Your estimation request has been received. We will get back to you as soon as possible.",
    };


    const handleSubmit = async () => {
        if (validateForm()) {
            setIsSubmitting(true); // Active le loader

            try {
                const form = new FormData();

                // Champs texte
                form.append('type', type);
                form.append('fullName', formData.fullName);
                form.append('email', formData.email);
                form.append('phone', formData.phone);
                form.append('brand', formData.brand);
                form.append('model', formData.model);
                form.append('trim', formData.trim);
                form.append('year', formData.year);
                form.append('description', formData.description);
                form.append('preferredLanguage', formData.preferredLanguage);
                form.append('contactMethod', formData.contactMethod);

                // Images
                formData.images.forEach((image) => {
                    form.append('images', image);
                });

                const response = await axios.post(
                    `${AppURL}/api/estimations/create`,
                    form,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                if (response.data.success) {
                    // Afficher la notification de succès
                    const notification = document.createElement('div');
                    notification.className = `
                        fixed top-4 right-4 z-50
                        bg-white border-l-4 border-green-500
                        shadow-lg rounded-lg overflow-hidden
                        w-full max-w-xs
                        transform transition-all duration-300
                        opacity-0 translate-y-2
                    `;

                    notification.innerHTML = `
                        <div class="flex items-start p-4">
                            <div class="flex-shrink-0">
                                <svg class="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div class="ml-3 w-0 flex-1 pt-0.5">
                                <p class="text-sm font-medium text-gray-900">
                                    ${language === 'fr' ? 'Succès!' : 'Success!'}
                                </p>
                                <p class="mt-1 text-sm text-gray-500">
                                    ${successMessages[language as keyof typeof successMessages] || successMessages['fr']}
                                </p>
                            </div>
                            <div class="ml-4 flex-shrink-0 flex">
                                <button class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none">
                                    <span class="sr-only">Fermer</span>
                                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 text-right">
                            <button type="button" class="text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none">
                                ${language === 'fr' ? 'Fermer' : 'Close'}
                            </button>
                        </div>
                    `;

                    document.body.appendChild(notification);

                    // Animation d'entrée
                    setTimeout(() => {
                        notification.classList.remove('opacity-0', 'translate-y-2');
                        notification.classList.add('opacity-100', 'translate-y-0');
                    }, 10);

                    // Gestion du clic sur le bouton fermer
                    const closeBtn = notification.querySelector('button');
                    closeBtn?.addEventListener('click', () => {
                        notification.classList.add('opacity-0', 'translate-y-2');
                        setTimeout(() => {
                            notification.remove();
                            onClose();
                        }, 300);
                    });

                    // Fermeture automatique
                    setTimeout(() => {
                        notification.classList.add('opacity-0', 'translate-y-2');
                        setTimeout(() => {
                            notification.remove();
                            onClose();
                        }, 300);
                    }, 5000);

                } else {
                    console.warn('Erreur :', response.data.message);
                }
            } catch (error: any) {
                console.error('Erreur lors de l\'envoi :', error.response?.data || error.message);
            } finally {
                setIsSubmitting(false); // Désactive le loader dans tous les cas
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="mt-20 fixed inset-0 z-50 flex items-center justify-center  bg-opacity-50">
            {isSubmitting && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                        <p className="text-gray-700">
                            {language === 'fr' ? 'Envoi en cours...' : 'Submitting...'}
                        </p>
                    </div>
                </div>
            )}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white w-full max-w-3xl mx-4 p-6 rounded-lg shadow-lg overflow-y-auto max-h-[80vh]">
                <h2 className="text-3xl font-extrabold text-yellow-500 mb-4">
                    {language === "fr"
                        ? type === "Mécanique"
                            ? "Estimation Mécanique"
                            : "Estimation Carrosserie"
                        : type === "Mécanique"
                            ? "Mechanical Estimate"
                            : "Bodywork Estimate"}
                </h2>

                <form>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Nom complet" : "Full Name"}</label>
                        <input
                            type="Name"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Email" : "Email"}</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Numéro de Téléphone" : "Phone Number"}</label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Marque du Véhicule" : "Vehicle Brand"}</label>
                        <select
                            name="brand"
                            value={formData.brand}
                            onChange={handleSelectChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        >
                            <option value="">{language === "fr" ? "Sélectionnez une marque" : "Select a brand"}</option>
                            {Object.keys(vehicleBrands).map((brand) => (
                                <option key={brand} value={brand}>
                                    {brand}
                                </option>
                            ))}
                        </select>
                    </div>

                    {formData.brand && (
                        <div className="mb-4">
                            <label className="block text-yellow-500">{language === "fr" ? "Modèle du Véhicule" : "Vehicle Model"}</label>
                            <select
                                name="model"
                                value={formData.model}
                                onChange={handleSelectChange}
                                className="w-full p-2 border rounded bg-gray-800 text-white"
                                required
                            >
                                <option value="">{language === "fr" ? "Sélectionnez un modèle" : "Select a model"}</option>
                                {vehicleBrands[formData.brand].map((model) => (
                                    <option key={model} value={model}>
                                        {model}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* AJOUTER LE CHAMP TRIM ICI */}
                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Finition du Véhicule" : "Vehicle Trim"}</label>
                        <input
                            type="text"
                            name="trim"
                            value={formData.trim}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            placeholder={language === "fr" ? "Entrez la finition" : "Enter the trim"}
                            required
                        />
                    </div>


                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Année du Véhicule" : "Vehicle Year"}</label>
                        <select
                            name="year"
                            value={formData.year}
                            onChange={handleSelectChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        >
                            <option value="">{language === "fr" ? "Sélectionnez une année" : "Select a year"}</option>
                            {Array.from({ length: 26 }, (_, index) => 2000 + index).map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Nouveau champ : Langue de communication préférée */}
                    <div className="mb-4">
                        <label className="block text-yellow-500">
                            {language === 'fr' ? 'Langue de communication préférée' : 'Preferred communication language'}
                        </label>
                        <select
                            id="preferredLanguage"
                            name="preferredLanguage"
                            value={formData.preferredLanguage}
                            onChange={handleSelectChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white">

                            <option value="fr">Français</option>
                            <option value="en">English</option>
                        </select>
                    </div>


                    {/* Nouveau champ : Méthode de contact préférée */}
                    <div className="mb-4">
                        <label className="block text-yellow-500">
                            {language === 'fr' ? 'Méthode de contact préférée' : 'Preferred contact method'}
                        </label>
                        <select
                            id="contactMethod"
                            name="contactMethod"
                            value={formData.contactMethod}
                            onChange={handleSelectChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white">

                            <option value="email">Email</option>
                            <option value="phone">{language === 'fr' ? 'Téléphone' : 'Phone'}</option>
                            <option value="both">{language === 'fr' ? 'Les deux' : 'Both'}</option>
                        </select>
                    </div>

                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Description du Problème" : "Problem Description"}</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                            required
                        ></textarea>
                    </div>


                    <div className="mb-4">
                        <label className="block text-yellow-500">{language === "fr" ? "Télécharger des Images" : "Upload Images"}</label>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="w-full p-2 border rounded bg-gray-800 text-white"
                        />
                        {formData.images.length > 0 && (
                            <div className="mt-2 flex space-x-2">
                                {formData.images.map((image, index) => (
                                    <div key={index} className="relative">
                                        <img
                                            src={URL.createObjectURL(image)}
                                            alt="Uploaded"
                                            className="w-20 h-20 object-cover rounded"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>



                    {error && <p className="text-red-500">{error}</p>}

                    <div className="flex justify-end space-x-4 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition duration-300"
                        >
                            {language === "fr" ? "Annuler" : "Cancel"}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded hover:bg-yellow-600 transition duration-300"
                        >
                            {language === "fr" ? "Soumettre" : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EstimationModal;
